import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiMenu, FiSearch, FiX } from "react-icons/fi";
import { HiOutlineShoppingBag } from "react-icons/hi";
import { CartContext } from "./CartContext";

const shopSearch = (term) => `/shop?search=${encodeURIComponent(term)}`;

const navigationItems = [
  {
    id: "shop-all",
    label: "Shop All",
    to: "/shop",
  },
  {
    id: "notebooks",
    label: "Notebooks",
    children: [
      { label: "Notebooks", to: shopSearch("notebook") },
      { label: "Planners", to: shopSearch("planner") },
      { label: "Reading and Recipe Logs", to: shopSearch("log") },
    ],
  },
  {
    id: "writing",
    label: "Writing Tools",
    children: [
      { label: "Fountain Pens", to: shopSearch("fountain pen") },
      { label: "Calligraphy", to: shopSearch("calligraphy") },
      { label: "Markers and Pencils", to: shopSearch("marker") },
    ],
  },
  {
    id: "accessories",
    label: "Pen Accessories",
    children: [
      { label: "Pen Cases", to: shopSearch("pen case") },
      {
        label: "Stationery and Writing Accessories",
        to: shopSearch("stationery"),
      },
      { label: "Desk Accessories", to: shopSearch("desk") },
    ],
  },
  {
    id: "creative-tools",
    label: "Colorful Creative Tools",
    to: shopSearch("creative tools"),
  },
  {
    id: "pens",
    label: "Pens and Writing Instruments",
    to: shopSearch("pen"),
  },

  {
    id: "contact",
    label: "Contact",
    children: [
      { label: "Contact Us", to: "/contact" },

      { label: "About ioomparade", to: "/about" },
    ],
  },
  {
    id: "more",
    label: "More",
    children: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Terms of Service", to: "/terms" },
      { label: "FAQs", to: "/faqs" },
      { label: "Delivery", to: "/shipping" },
      { label: "Returns", to: "/returns" },
    ],
  },
];

function MenuAnchor({ item, onNavigate, className }) {
  if (item.href) {
    return (
      <a
        className={className}
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {item.label}
      </a>
    );
  }

  return (
    <Link className={className} to={item.to} onClick={onNavigate}>
      {item.label}
    </Link>
  );
}

function Header() {
  const { cartItems } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const menuButtonRef = useRef(null);
  const menuOverlayRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  const cartItemCount = (cartItems || []).reduce((total, item) => {
    const quantity = Number(item?.quantity);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);

  useEffect(() => {
    const updateScrollState = () => setIsScrolled(window.scrollY > 24);
    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollState);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenAccordion(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFirstItem = window.requestAnimationFrame(() => {
      menuOverlayRef.current?.querySelector("[data-menu-focus]")?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsMenuOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
        return;
      }

      if (event.key !== "Tab") return;

      const overlayFocusables = Array.from(
        menuOverlayRef.current?.querySelectorAll(
          "a[href], button:not([disabled]), input:not([disabled])"
        ) || []
      ).filter((element) => element.offsetParent !== null);
      const focusables = [menuButtonRef.current, ...overlayFocusables].filter(
        Boolean
      );

      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFirstItem);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenAccordion(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchValue.trim();
    setIsMenuOpen(false);
    setOpenAccordion(null);
    navigate(query ? shopSearch(query) : "/shop");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !isScrolled && !isMenuOpen;
  const headerClasses = [
    "site-header",
    isHome ? "site-header--home" : "",
    isScrolled ? "site-header--scrolled" : "",
    isTransparent ? "site-header--transparent" : "",
    isMenuOpen ? "menu-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header className={headerClasses}>
        <div className="header-container">
          <button
            ref={menuButtonRef}
            type="button"
            className="menu-toggle"
            aria-expanded={isMenuOpen}
            aria-controls="site-navigation"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {isMenuOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiMenu aria-hidden="true" />
            )}
          </button>

          <Link
            to="/"
            className="logo-link"
            onClick={closeMenu}
            aria-label="ioomparade home"
          >
            <img
              src="/head/ioomparade-logo.svg"
              alt="ioomparade"
              width="186"
              height="42"
            />
          </Link>

          <Link
            to="/cartpage"
            className="cart-link"
            onClick={closeMenu}
            aria-label={`Shopping cart, ${cartItemCount} ${
              cartItemCount === 1 ? "item" : "items"
            }`}
          >
            <HiOutlineShoppingBag aria-hidden="true" />
            {cartItemCount > 0 && (
              <span className="cart-count" aria-hidden="true">
                {cartItemCount > 99 ? "99+" : cartItemCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      <div
        ref={menuOverlayRef}
        id="site-navigation"
        className={`navigation-overlay ${isMenuOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={!isMenuOpen}
      >
        <nav className="navigation-panel" aria-label="Main navigation">
          <ul className="navigation-links">
            {navigationItems.map((item, index) => {
              const panelId = `navigation-panel-${item.id}`;
              const isExpanded = openAccordion === item.id;

              return (
                <li className="navigation-item" key={item.id}>
                  {item.children ? (
                    <>
                      <button
                        type="button"
                        className="navigation-primary navigation-accordion"
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        data-menu-focus={index === 0 ? "true" : undefined}
                        onClick={() =>
                          setOpenAccordion((current) =>
                            current === item.id ? null : item.id
                          )
                        }
                      >
                        <span>{item.label}</span>
                        <span className="accordion-mark" aria-hidden="true">
                          {isExpanded ? "−" : "+"}
                        </span>
                      </button>
                      <div
                        id={panelId}
                        className="navigation-submenu"
                        hidden={!isExpanded}
                      >
                        <ul>
                          {item.children.map((child) => (
                            <li key={child.label}>
                              <MenuAnchor item={child} onNavigate={closeMenu} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <MenuAnchor
                      item={item}
                      className="navigation-primary"
                      onNavigate={closeMenu}
                      data-menu-focus={index === 0 ? "true" : undefined}
                    />
                  )}
                </li>
              );
            })}
          </ul>

          <form
            className="navigation-search"
            role="search"
            onSubmit={handleSearch}
          >
            <label className="header-visually-hidden" htmlFor="header-search">
              Search products
            </label>
            <input
              id="header-search"
              type="search"
              value={searchValue}
              placeholder="Search"
              autoComplete="off"
              onChange={(event) => setSearchValue(event.target.value)}
            />
            <button type="submit" aria-label="Search">
              <FiSearch aria-hidden="true" />
            </button>
          </form>
        </nav>
      </div>
    </>
  );
}

export default Header;
